import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { PdfWatermark } from './PdfWatermark';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
    position: 'relative'
  },
  watermarkContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: -1,
    opacity: 0.08
  },
  watermarkImage: {
    width: 350,
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 20,
  },
  logo: {
    height: 80,
    width: 250,
    objectFit: 'contain'
  },
  infoContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20
  },
  boxInfo: {
    width: '48%',
    padding: 10,
    border: '1pt solid #ccc',
    borderRadius: 4,
    backgroundColor: '#f9f9f9',
  },
  boxTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 11,
    marginBottom: 6,
    color: '#333',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    paddingBottom: 4
  },
  teamName: {
    fontSize: 12,
    marginBottom: 4,
    fontFamily: 'Helvetica-Bold'
  },
  textRow: {
    marginBottom: 3,
    color: '#333'
  },
  titleSection: {
    alignItems: 'center',
    marginBottom: 20,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#eee'
  },
  reportTitle: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 8,
    color: '#1a1a1a'
  },
  reportMeta: {
    fontSize: 10,
    color: '#444',
    marginBottom: 2
  },
  docHeaderInfo: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 10
  },
  reportSubTitle: {
    fontSize: 16,
    fontFamily: 'Helvetica-Bold',
    marginTop: 5,
    marginBottom: 5,
    textAlign: 'center',
    color: '#333'
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    fontSize: 8,
    color: '#888',
    textAlign: 'center',
    borderTopWidth: 1,
    borderTopColor: '#eee',
    paddingTop: 8
  },
  clientInfo: {
    marginTop: 10,
    padding: 15,
    backgroundColor: '#f8f9fa',
    borderRadius: 4,
    border: '1pt solid #eee'
  },
  clientName: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 12,
    marginBottom: 6
  },
  table: {
    display: "flex",
    width: "100%",
    marginTop: 25,
  },
  tableRowHeader: {
    display: 'flex',
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    paddingBottom: 6,
    marginBottom: 6
  },
  tableRow: {
    display: 'flex',
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    paddingVertical: 6,
  },
  colDesc: { width: "45%", paddingRight: 10 },
  colQty: { width: "15%", textAlign: 'center' },
  colPrice: { width: "20%", textAlign: 'right' },
  colTotal: { width: "20%", textAlign: 'right' },
  colHeaderFormat: { fontFamily: 'Helvetica-Bold' },
  totalsSection: {
    marginTop: 25,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    width: '100%'
  },
  totalRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 6,
    width: "60%"
  },
  totalLabel: {
    width: "70%",
    textAlign: 'right',
    paddingRight: 10,
    color: '#444'
  },
  totalValue: {
    width: "30%",
    textAlign: 'right'
  },
  grandTotalLevel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 12,
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#000'
  }
});

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

const BillingPdf = ({ summary, teamData, driverData, logoBase64, t, showWatermark = true }) => {
  const laborTotal = summary.totals?.labor || 0;
  const partsTotal = summary.totals?.parts || 0;
  const manualTotal = summary.totals?.manual || 0;
  const grandTotal = summary.totals?.grandTotal || 0;
  
  const allItems = [
    ...(summary.lineItems_labor || []).map(i => ({ desc: i.taskDescription || 'N/A', qty: `${i.ore} h`, price: i.costoOrario, total: i.total })),
    ...(summary.lineItems_parts || []).map(i => ({ desc: i.name || 'N/A', qty: i.quantity, price: i.prezzoVenditaUnitario, total: i.total })),
    ...(summary.lineItems_manual || []).map(i => ({ desc: i.description || 'N/A', qty: 1, price: i.amount, total: i.amount }))
  ];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {showWatermark && <PdfWatermark />}

        {/* Header - Centered Logo */}
        <View style={styles.headerRow}>
          {logoBase64 ? (
            <Image src={logoBase64} style={styles.logo} allowDangerousPaths />
          ) : (
            <Text style={{fontSize: 24, fontFamily: 'Helvetica-Bold', marginTop: 20}}>{teamData?.nomeTeam || 'Team Name'}</Text>
          )}
        </View>

        {/* Info Container - Due Riquadri Paralleli */}
        <View style={styles.infoContainer}>
          {/* Team Box */}
          <View style={styles.boxInfo}>
            <Text style={styles.boxTitle}>{t('export.receipt.from')} (Team)</Text>
            <Text style={styles.teamName}>{teamData?.nomeTeam || 'Team Name'}</Text>
            <Text style={styles.textRow}>{teamData?.address || '-'}</Text>
            <Text style={styles.textRow}>{teamData?.city || '-'}</Text>
            <Text style={styles.textRow}>{t('export.receipt.vat')}: {teamData?.piva || 'N/A'}</Text>
          </View>

          {/* Driver Box (Client) */}
          <View style={styles.boxInfo}>
            <Text style={styles.boxTitle}>{t('export.receipt.to')} (Client)</Text>
            <Text style={styles.teamName}>{driverData?.nomeCompleto || 'N/A'}</Text>
            <Text style={styles.textRow}>{driverData?.address || '-'}</Text>
            <Text style={styles.textRow}>{driverData?.city || '-'} {driverData?.postalCode || ''}</Text>
            <Text style={styles.textRow}>{t('export.receipt.vat')}: {driverData?.vatNumber || '-'} | {t('export.receipt.fiscal_code')}: {driverData?.fiscalCode || '-'}</Text>
            <Text style={styles.textRow}>{t('export.receipt.sdi_code')}: {driverData?.sdiCode || '-'} | {t('export.receipt.email')}: {driverData?.contactEmail || '-'}</Text>
          </View>
        </View>

        {/* Title */}
        <View style={styles.titleSection}>
          <View style={styles.docHeaderInfo}>
            <Text style={styles.reportMeta}>{t('columns.title')}: {summary?.titolo || 'N/A'}</Text>
            <Text style={styles.reportMeta}>
              {t('export.receipt.date')} {summary?.createdAt ? format(new Date(summary.createdAt), 'dd/MM/yyyy') : 'N/A'}
            </Text>
          </View>
          <Text style={styles.reportSubTitle}>{t('export.receipt.cost_summary_head', 'Riepilogo Costi')}</Text>
        </View>

        {/* Table */}
        <View style={styles.table}>
          <View style={styles.tableRowHeader}>
            <Text style={[styles.colDesc, styles.colHeaderFormat]}>{t('export.receipt.col_desc')}</Text>
            <Text style={[styles.colQty, styles.colHeaderFormat]}>{t('export.receipt.col_qty')}</Text>
            <Text style={[styles.colPrice, styles.colHeaderFormat]}>{t('export.receipt.col_unit_price')}</Text>
            <Text style={[styles.colTotal, styles.colHeaderFormat]}>{t('export.receipt.col_total')}</Text>
          </View>
          
          {allItems.map((item, index) => (
            <View style={styles.tableRow} key={index}>
              <Text style={styles.colDesc}>{item.desc}</Text>
              <Text style={styles.colQty}>{item.qty}</Text>
              <Text style={styles.colPrice}>{formatCurrency(item.price)}</Text>
              <Text style={styles.colTotal}>{formatCurrency(item.total)}</Text>
            </View>
          ))}
        </View>

        {/* Totals */}
        <View style={styles.totalsSection}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>{t('export.receipt.subtotal_labor')}</Text>
            <Text style={styles.totalValue}>{formatCurrency(laborTotal)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>{t('export.receipt.subtotal_parts')}</Text>
            <Text style={styles.totalValue}>{formatCurrency(partsTotal)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>{t('export.receipt.subtotal_manual')}</Text>
            <Text style={styles.totalValue}>{formatCurrency(manualTotal)}</Text>
          </View>
          <View style={[styles.totalRow, styles.grandTotalLevel]}>
            <Text style={[styles.totalLabel, {fontFamily: 'Helvetica-Bold'}]}>{t('export.receipt.grand_total')}</Text>
            <Text style={[styles.totalValue, {fontFamily: 'Helvetica-Bold'}]}>{formatCurrency(grandTotal)}</Text>
          </View>
        </View>

        {/* Footer fisso su tutte le pagine con Numeri Pagina */}
        <Text style={styles.footer} fixed render={({ pageNumber, totalPages }) => (
          `${teamData?.nomeTeam || 'Team'} - ${summary?.titolo || '-'} - ${summary?.createdAt ? format(new Date(summary.createdAt), 'dd/MM/yyyy') : '-'} - ${driverData?.nomeCompleto || '-'} - Pag ${pageNumber}/${totalPages}`
        )} />
        
      </Page>
    </Document>
  );
};

export default BillingPdf;
